import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ApplicationFormStep } from '@/components/Applications/ApplicationForm/types';

interface MultiStepApplicationsContextInterface {
  data: any;
  currentStep: number;
  completedSteps: number[];
  formJSON: ApplicationFormStep[];
  setFormJSON: React.Dispatch<React.SetStateAction<ApplicationFormStep[]>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  updateData: (values: any) => void;
}

const defaultContextValue: MultiStepApplicationsContextInterface = {
  data: {},
  currentStep: 0,
  completedSteps: [],
  formJSON: [],
  updateData: () => {
    throw new Error('Not Implemented');
  },
  setCurrentStep: () => {
    throw new Error('Not Implemented');
  },
  setFormJSON: () => {
    throw new Error('Not Implemented');
  },
};

const MultiStepApplicationsContext = createContext<MultiStepApplicationsContextInterface>(defaultContextValue);

const useMultiStepApplicationProvider = (initialValues?: any): MultiStepApplicationsContextInterface => {
  const [data, setData] = useState<any>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [formJSON, setFormJSON] = useState<ApplicationFormStep[]>([]);
  const updateData = useCallback(
    (values: any) => {
      setData((prev: any) => ({ ...prev, ...values }));
      setCompletedSteps((prev) => [...prev, currentStep]);
    },
    [currentStep]
  );

  useEffect(() => {
    if (!data) {
      updateData(initialValues);
    }
  }, [initialValues, updateData, data]);

  useEffect(
    () => () => {
      setData({});
      setCurrentStep(0);
      setCompletedSteps([]);
    },
    []
  );

  return {
    data,
    currentStep,
    completedSteps,
    formJSON,
    updateData,
    setCurrentStep,
    setFormJSON,
  };
};

interface MultiStepApplicationProviderProps {
  children?: React.ReactNode;
  initialValues?: any;
}

export const MultiStepApplicationProvider = (props: MultiStepApplicationProviderProps) => {
  const { children, initialValues } = props;
  const value = useMultiStepApplicationProvider(initialValues);
  return <MultiStepApplicationsContext.Provider value={value}>{children}</MultiStepApplicationsContext.Provider>;
};

export const useMultiStepApplication = () => useContext(MultiStepApplicationsContext);
